import * as yup from "yup";
import { formatNumber, validateCPF, validateDocument } from "./helpers";

//schema validation purchasingForm
/**
 * @description Esquema de validação para o formulário de compra.
 * Utiliza a biblioteca Yup para definir as regras de validação dos campos.
 * Cada campo possui regras específicas, como obrigatoriedade, formato e tamanho.
 * As mensagens de erro são personalizadas para cada validação.
 */

//function to validate purchasingForm
export const purchasingScheme = yup
  .object({
    vouch_binu_documento: yup
      .string()
      .required("Preencha o documento.")
      .test(
        "documento válido",
        "* Preencha o documento corretamente",
        (value) => {
          if (value.length >= 11 && value.length <= 18) {
            if (validateDocument(value)) {
              return true;
            } else {
              return false;
            }
          }
        },
      ),
    vouch_dmnu_cpfresponsavel: yup
      .string()
      .required("Preencha o CPF do responsável.")
      .test("CPF válido", "* Preencha o CPF corretamente", (value) => {
        if (value.length >= 11 && value.length <= 14) {
          if (validateDocument(value)) {
            return true;
          } else {
            return false;
          }
        }
        return false;
      }),
    vouch_vcnm_razaosocial: yup
      .string()
      .required("Preencha a razão social.")
      .test("Dados válidos", "* Dados inválidos", (value) => {
        if (value.length >= 3) {
          return true;
        }
        return false;
      }),
    vouch_vcnm_responsavel: yup
      .string()
      .required("Preencha o responsável.")
      .test("Dados válidos", "* Dados inválidos", (value) => {
        if (value.length >= 3) {
          return true;
        }
        return false;
      }),
    vouch_vcnm_fantasia: yup.string().when("vouch_dmtp_documento", {
      is: (doc: string) => doc && doc[0]?.toUpperCase() === "F",
      then: (schema) => schema.notRequired(),
      otherwise: (schema) =>
        schema
          .required("Preencha o nome fantasia.")
          .test("Nome fantasia válido", "* Nome fantasia inválido", (value) => {
            if (value.length >= 1) {
              return true;
            }
            return false;
          }),
    }),
    vouch_bite_celular: yup
      .string()
      .required("Preencha o celular.")
      .test("Celular válido", "* Preencha o Celular corretamente", (value) => {
        if (formatNumber(value).length >= 10 && value != "0000000000") {
          return true;
        }
        return false;
      }),
    vouch_vcem_responsavel: yup
      .string()
      .email("E-mail inválido")
      .required("Preencha o email.")
      .test("E-mail válido", "* Preencha o E-mail corretamente", (value) => {
        if (value.length >= 3) {
          return true;
        }
        return false;
      }),
    vouch_chcd_cep: yup
      .string()
      .required("Preencha o CEP.")
      .test("CEP válido", "* Preencha o CEP corretamente", (value) => {
        if (formatNumber(value).length >= 8) {
          return true;
        }
        return false;
      }),
    vouch_vcds_endereco: yup
      .string()
      .required("Preencha o endereço.")
      .test(
        "Endereço válido",
        "* Preencha o Endereço corretamente",
        (value) => {
          if (value.length >= 3) {
            return true;
          }
          return false;
        },
      ),
    vouch_vcnu_numero: yup
      .string()
      .required("Preencha o número.")
      .test("Número válido", "* Preencha o Número corretamente", (value) => {
        if (parseInt(formatNumber(value)) !== 0) {
          return true;
        }
        return false;
      }),
    vouch_vcnm_bairro: yup
      .string()
      .required("Preencha o bairro.")
      .test("Bairro válido", "* Preencha o Bairro corretamente", (value) => {
        if (value.length >= 3) {
          return true;
        }
        return false;
      }),
    vouch_vcnm_cidade: yup
      .string()
      .required("Preencha a cidade.")
      .test("Cidade válido", "* Preencha a Cidade corretamente", (value) => {
        if (value.length >= 3) {
          return true;
        }
        return false;
      }),
    vouch_chcd_uf: yup
      .string()
      .required("Preencha o Estado.")
      .test("Estado válido", "* Selecione um estado", (value) => {
        if (value) {
          return true;
        }
        return false;
      }),
  })
  .required();

//schema validation FormInitial
export const initialSchema = yup
  .object({
    document: yup
      .string()
      .required("Preencha o documento.")
      .test("documento-válido", "* Documento inválido", function (value) {
        if (!value) return false;

        // 1. Remova a formatação mantendo letras e números
        const cleanValue = value.replace(/[^a-zA-Z0-9]/g, "");

        const { typeDocument } = this.parent;

        if (typeDocument === "F") {
          // CPF continua sendo apenas numérico (11 dígitos)
          return /^\w{11}$/.test(cleanValue) && validateCPF(cleanValue);
        }

        if (typeDocument === "J") {
          // CNPJ Alfanumérico (14 caracteres)
          // As últimas 2 posições devem ser números (Dígitos Verificadores)
          const isLengthOk = cleanValue.length === 14;
          const dvIsNumeric = /^\w+$/.test(cleanValue.slice(-2));

          // Importante: validar função 'validateDocument' (ou 'validateCNPJ')
          // já implementa o novo cálculo de pesos para caracteres alfanuméricos (tabela ASCII - 48).
          return isLengthOk && dvIsNumeric && validateDocument(cleanValue);
        }
        return false;
      }),
    voucher: yup
      .string()
      .required("Preencha o número do cupom.")
      .test(
        "Número do cupom válido",
        "* Número do cupom inválido",
        (value) => {
          if (value.length >= 10) {
            return true;
          }
          return false;
        },
      ),
    typeDocument: yup
      .string()
      .required("Preencha o tipo de documento.")
      .test(
        "Tipo de documento válido",
        "* Selecione um tipo de documento",
        (value) => {
          if (value) {
            return true;
          }
          return false;
        },
      ),
  })
  .required();
